import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * Modo claro/escuro. Dark mode é nativo desde o início (requisito do escopo),
 * então a preferência é resolvida antes da primeira renderização e persistida.
 */

export type ColorScheme = 'light' | 'dark';
export type ColorSchemePreference = ColorScheme | 'system';

const STORAGE_KEY = 'titularis:color-scheme';

function systemScheme(): ColorScheme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * A preferência guardada, ou o padrão de quem chamou.
 *
 * O padrão é parâmetro porque os dois lados do produto querem coisas
 * diferentes, e os dois estão certos. **Dentro do app**, seguir o sistema é o
 * comportamento esperado: é uma ferramenta de trabalho que a pessoa abre por
 * horas, muitas vezes de madrugada. **Na landing**, não: ela é uma peça de
 * apresentação, o desenho foi decidido em claro, e o visitante que chega de um
 * link no WhatsApp não escolheu ver a marca em modo escuro - o aparelho dele
 * escolheu por ele. Quem quiser troca no seletor, e a escolha fica guardada.
 */
function storedPreference(padrao: ColorSchemePreference): ColorSchemePreference {
  if (typeof window === 'undefined') return padrao;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : padrao;
}

interface ColorSchemeContextValue {
  scheme: ColorScheme;
  preference: ColorSchemePreference;
  setPreference: (preference: ColorSchemePreference) => void;
  toggle: () => void;
}

const ColorSchemeContext = createContext<ColorSchemeContextValue | null>(null);

export function ColorSchemeProvider({
  children,
  padrao = 'system',
}: {
  children: ReactNode;
  /** O que vale antes de a pessoa escolher. O app segue o sistema; a landing, claro. */
  padrao?: ColorSchemePreference;
}) {
  const [preference, setPreferenceState] = useState<ColorSchemePreference>(() =>
    storedPreference(padrao),
  );
  const [system, setSystem] = useState<ColorScheme>(systemScheme);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = (event: MediaQueryListEvent) => setSystem(event.matches ? 'dark' : 'light');
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, []);

  const setPreference = useCallback((next: ColorSchemePreference) => {
    setPreferenceState(next);
    window.localStorage.setItem(STORAGE_KEY, next);
  }, []);

  const scheme: ColorScheme = preference === 'system' ? system : preference;

  useEffect(() => {
    document.documentElement.dataset.colorScheme = scheme;
    document.documentElement.style.colorScheme = scheme;
  }, [scheme]);

  const value = useMemo<ColorSchemeContextValue>(
    () => ({
      scheme,
      preference,
      setPreference,
      toggle: () => setPreference(scheme === 'dark' ? 'light' : 'dark'),
    }),
    [scheme, preference, setPreference],
  );

  return <ColorSchemeContext.Provider value={value}>{children}</ColorSchemeContext.Provider>;
}

export function useColorScheme(): ColorSchemeContextValue {
  const context = useContext(ColorSchemeContext);
  if (!context) {
    throw new Error('useColorScheme precisa estar dentro de ColorSchemeProvider.');
  }
  return context;
}
